import type { ReactNode } from 'react';
import { Box, Button, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import StarsOutlinedIcon from '@mui/icons-material/StarsOutlined';
import { OBSIDIAN_BODY_FONT, OBSIDIAN_DISPLAY_FONT } from '../../../constants/obsidianDashboardTheme';

const GOLD = '#ffc965';
const MINT = '#00fdc1';
const ON_VARIANT = '#adaaaa';

const iconProps = {
    width: 24,
    height: 24,
    fill: 'none',
    stroke: 'currentColor',
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    strokeWidth: 1.8,
    viewBox: '0 0 24 24',
};

function ConnectIcon() {
    return (
        <svg {...iconProps} aria-hidden>
            <rect height="15" rx="2" width="10" x="7" y="5" />
            <path d="M10 8h4" />
            <circle cx="12" cy="17" fill="currentColor" r="0.7" />
            <circle cx="4" cy="6" r="2.2" stroke={MINT} strokeWidth="1.4" />
            <path d="M19 4l2 3h-3z" stroke="#ecaa00" strokeWidth="1.2" />
            <circle cx="20" cy="16" r="2.2" stroke={MINT} strokeWidth="1.4" />
        </svg>
    );
}

function PriceIcon() {
    return (
        <svg {...iconProps} aria-hidden>
            <path d="M3 10V4a1 1 0 0 1 1-1h6l10 10-7 7L3 10z" />
            <circle cx="7.5" cy="7.5" fill="currentColor" r="1.5" />
            <path d="M16 14l2.5 2.5a2 2 0 1 1-2.8 2.8L13 16.5" stroke={MINT} strokeWidth="1.5" />
        </svg>
    );
}

function CampaignIcon() {
    return (
        <svg {...iconProps} aria-hidden>
            <rect height="13" rx="2" width="18" x="3" y="6" />
            <path d="M3 8l9 6 9-6" />
            <path d="M12 2v2M19 3l-1 1M5 3l1 1" stroke={MINT} strokeWidth="1.5" />
        </svg>
    );
}

function DeliverIcon() {
    return (
        <svg {...iconProps} aria-hidden>
            <rect height="20" rx="3" width="12" x="6" y="2" />
            <circle cx="12" cy="11" r="3.2" strokeWidth="1.5" />
            <circle cx="12" cy="11" fill={MINT} r="1" stroke="none" />
            <path d="M9 5h6" strokeWidth="1.2" />
        </svg>
    );
}

function PaidIcon() {
    return (
        <svg {...iconProps} aria-hidden>
            <path d="M2 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V7z" />
            <path d="M16 12h4" stroke={MINT} strokeWidth="2.2" />
            <circle cx="18" cy="12" fill={MINT} r="0.8" stroke="none" />
            <path d="M9 3v4M7 5l2-2 2 2" stroke="#ecaa00" strokeWidth="1.4" />
        </svg>
    );
}

const STEPS: { title: string; chip: string; chipMint?: boolean; chipGold?: boolean; body: string; icon: ReactNode }[] =
    [
        {
            title: '1. Connect',
            chip: 'Step 01',
            chipGold: true,
            body: 'Link Instagram, TikTok, or YouTube so brands can see who you are and your reach.',
            icon: <ConnectIcon />,
        },
        {
            title: '2. Update your profile',
            chip: 'Step 02',
            body: 'Update your profile with your creator details, and price your work.',
            icon: <PriceIcon />,
        },
        {
            title: '3. Get a campaign',
            chip: 'Step 03',
            body: 'Brands invite you, or you apply to an open campaign.',
            icon: <CampaignIcon />,
        },
        {
            title: '4. Deliver',
            chip: 'Step 04',
            body: 'Make the content and send it in the job workspace.',
            icon: <DeliverIcon />,
        },
        {
            title: '5. Get paid',
            chip: 'Payout',
            chipMint: true,
            body: 'Campaign payout lands in your balance. Cash out when it is ready. Earn bonus commission on attributed sales.',
            icon: <PaidIcon />,
        },
    ];

export function BuzzBridgeOnboardingMobile({ onConnectClick }: { onConnectClick: () => void }) {
    return (
        <Box sx={{ width: '100%', maxWidth: 420, mx: 'auto', pb: 4 }}>
            <Box sx={{ px: 0.5, pt: 0.5, pb: 4, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 999,
                        bgcolor: '#201f1f',
                        width: 'fit-content',
                    }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: MINT }} />
                    <Typography
                        sx={{
                            fontFamily: OBSIDIAN_BODY_FONT,
                            fontSize: 12,
                            fontWeight: 700,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            color: MINT,
                        }}>
                        Creator Launchpad
                    </Typography>
                </Box>
                <Typography
                    component="h1"
                    sx={{
                        fontFamily: OBSIDIAN_DISPLAY_FONT,
                        fontWeight: 800,
                        fontSize: 30,
                        lineHeight: 1.15,
                        letterSpacing: '-0.03em',
                        color: '#fff',
                    }}>
                    Get hired.
                    <br />
                    Make the content.
                    <br />
                    <Box
                        component="span"
                        sx={{
                            background: `linear-gradient(90deg, ${GOLD}, #feb700)`,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                        }}>
                        Get paid.
                    </Box>
                </Typography>
                <Typography
                    sx={{ fontFamily: OBSIDIAN_BODY_FONT, fontSize: 14, lineHeight: 1.6, color: ON_VARIANT, pt: 0.5 }}>
                    Thspian connects you with brands that pay creators for UGC. Connect a social account to start.
                </Typography>
            </Box>

            <Box sx={{ position: 'relative', px: 0.5, py: 2 }}>
                <Box
                    sx={{
                        position: 'absolute',
                        left: 28,
                        top: 32,
                        bottom: 48,
                        width: 2,
                        borderRadius: 999,
                        background: `linear-gradient(180deg, ${GOLD}, #ecaa00, rgba(255,201,101,0.2))`,
                    }}
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, position: 'relative' }}>
                    {STEPS.map(step => (
                        <Box key={step.title} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <Box
                                sx={{
                                    position: 'relative',
                                    zIndex: 1,
                                    flexShrink: 0,
                                    width: 48,
                                    height: 48,
                                    borderRadius: '50%',
                                    bgcolor: '#262626',
                                    color: GOLD,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 8px 16px rgba(0,0,0,0.35)',
                                }}>
                                {step.icon}
                            </Box>
                            <Box sx={{ flex: 1, bgcolor: '#131313', p: 2, borderRadius: 1.5 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 1,
                                        mb: 0.5,
                                    }}>
                                    <Typography
                                        sx={{
                                            fontFamily: OBSIDIAN_DISPLAY_FONT,
                                            fontWeight: 700,
                                            fontSize: 16,
                                            color: GOLD,
                                        }}>
                                        {step.title}
                                    </Typography>
                                    <Box
                                        component="span"
                                        sx={{
                                            flexShrink: 0,
                                            px: 1,
                                            py: 0.25,
                                            borderRadius: 999,
                                            fontFamily: OBSIDIAN_BODY_FONT,
                                            fontSize: 10,
                                            fontWeight: 700,
                                            letterSpacing: '0.06em',
                                            textTransform: 'uppercase',
                                            bgcolor: step.chipMint
                                                ? '#006c50'
                                                : step.chipGold
                                                ? 'rgba(255,201,101,0.1)'
                                                : '#201f1f',
                                            color: step.chipMint ? '#dfffef' : step.chipGold ? GOLD : ON_VARIANT,
                                        }}>
                                        {step.chip}
                                    </Box>
                                </Box>
                                <Typography
                                    sx={{
                                        fontFamily: OBSIDIAN_BODY_FONT,
                                        fontSize: 12,
                                        color: ON_VARIANT,
                                        lineHeight: 1.55,
                                    }}>
                                    {step.body}
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Box>

            <Box sx={{ mt: 2, px: 0.5 }}>
                <Box
                    sx={{
                        bgcolor: '#201f1f',
                        borderRadius: 1.5,
                        p: 1.75,
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.5,
                    }}>
                    <Box
                        sx={{
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            bgcolor: 'rgba(0,253,193,0.1)',
                            color: MINT,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}>
                        <StarsOutlinedIcon sx={{ fontSize: 16 }} />
                    </Box>
                    <Typography
                        sx={{ fontFamily: OBSIDIAN_BODY_FONT, fontSize: 12, color: ON_VARIANT, lineHeight: 1.45 }}>
                        <Box component="strong" sx={{ color: '#fff', fontWeight: 600 }}>
                            Bonus:
                        </Box>{' '}
                        Select campaigns also pay extra performance commission on sales or app installs.
                    </Typography>
                </Box>
            </Box>

            <Box
                sx={{
                    mt: 4,
                    px: 0.5,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    gap: 1.5,
                }}>
                <Typography
                    sx={{
                        fontFamily: OBSIDIAN_DISPLAY_FONT,
                        fontWeight: 800,
                        fontSize: 11,
                        letterSpacing: '0.16em',
                        textTransform: 'uppercase',
                        color: ON_VARIANT,
                    }}>
                    Ready to start?
                </Typography>
                <Button
                    fullWidth
                    onClick={onConnectClick}
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                        py: 2,
                        borderRadius: 999,
                        textTransform: 'none',
                        fontFamily: OBSIDIAN_DISPLAY_FONT,
                        fontWeight: 800,
                        fontSize: 16,
                        color: '#392700',
                        background: `linear-gradient(90deg, ${GOLD}, #feb700)`,
                        boxShadow: '0 4px 24px rgba(254,183,0,0.25)',
                        '&:hover': { background: `linear-gradient(90deg, #ffd080, #feb700)` },
                        '&:active': { transform: 'scale(0.98)' },
                    }}>
                    Connect your socials
                </Button>
                <Typography sx={{ fontFamily: OBSIDIAN_BODY_FONT, fontSize: 11, color: ON_VARIANT, lineHeight: 1.4 }}>
                    Instagram, TikTok, or YouTube. You can add the others later.
                </Typography>
            </Box>
        </Box>
    );
}
